<template>
    <BaseForm
        class="account-details form--stacked-actions"
        @submit="submitAccountDetails"
    >
        <template
            v-if="!isFreeAccountSignup"
            #title
        >
            Vul je gegevens in
        </template>

        <template
            v-else-if="isFreeAccountSignup"
            #title
        >
            Maak je gratis account aan
        </template>

        <template #content>
            <p v-if="isFreeAccountSignup">
                Bewaar je favorieten en log altijd eenvoudig in op je persoonlijke We Are Public-profiel.
            </p>

            <FieldValidator
                v-slot="{ field, meta, errors, handleReset }"
                name="email"
            >
                <BaseFormField
                    :required="true"
                    :errors="errors"
                >
                    <template #label>E-mail</template>

                    <BaseInput
                        v-bind="field"
                        type="email"
                        :validator="meta"
                        @change="submissionErrorMessage = null"
                        @input="submissionErrorMessage = null"
                        @clear="handleReset"
                    />
                </BaseFormField>
            </FieldValidator>

            <p v-if="membershipStatus === MEMBERSHIP_ACTIVE">
                Je hebt al een actieve We Are Public-pas.
                Klik
                <NuxtLink
                    to="/login"
                    class="account-details__login-link"
                >
                    hier
                </NuxtLink> om in te loggen.
            </p>

            <FieldValidator
                v-slot="{ field, meta, errors, handleReset }"
                name="password"
            >
                <BaseFormField
                    :required="true"
                    :errors="errors"
                >
                    <template #label>Kies een wachtwoord</template>
                    <BaseInput
                        v-bind="field"
                        type="password"
                        autocomplete="off"
                        :validator="meta"
                        @change="submissionErrorMessage = null"
                        @input="submissionErrorMessage = null"
                        @clear="handleReset"
                    />
                </BaseFormField>
            </FieldValidator>

            <FieldValidator
                v-slot="{ field, meta, errors, handleReset }"
                name="password_confirmation"
            >
                <BaseFormField
                    :errors="errors"
                >
                    <template #label>Herhaal wachtwoord</template>

                    <BaseInput
                        v-bind="field"
                        type="password"
                        autocomplete="off"
                        :validator="meta"
                        @change="submissionErrorMessage = null"
                        @input="submissionErrorMessage = null"
                        @clear="handleReset"
                    />

                    <template #info>
                        Je wachtwoord moet minimaal 8 tekens lang zijn.
                    </template>
                </BaseFormField>
            </FieldValidator>

            <FieldValidator
                v-if="studentNumberEnabled"
                v-slot="{ field, handleReset }"
                name="studentNumber"
            >
                <BaseFormField>
                    <template #label>{{ studentNumberLabel }}</template>

                    <BaseInput
                        v-bind="field"
                        type="text"
                        @clear="handleReset"
                    />

                    <template
                        #info
                    >
                        {{ studentNumberHelpText }}
                    </template>
                </BaseFormField>
            </FieldValidator>

            <FieldValidator
                v-slot="{ field, handleReset }"
                name="custom"
            >
                <BaseFormField v-if="customFieldEnabled">
                    <template #label>{{ customFieldLabel }}</template>

                    <BaseInput
                        v-bind="field"
                        type="text"
                        @clear="handleReset"
                    />

                    <template #info>{{ customFieldDescription }}</template>
                </BaseFormField>
            </FieldValidator>

            <BaseCheckbox
                v-model="consentGiven"
                :value="true"
                @change="submissionErrorMessage = null"
            >
                <!-- eslint-disable-next-line -->
                <span v-html="authorization" />
            </BaseCheckbox>

            <!-- eslint-disable vue/no-v-html -->
            <div
                v-if="submissionErrorMessage"
                class="form__error"
                v-html="submissionErrorMessage"
            />
            <!-- eslint-enable vue/no-v-html -->
        </template>

        <template #actions>
            <BaseButton
                v-if="!isFreeAccountSignup"
                :disabled="isSubmitting"
                type="submit"
                class="form__submit button--secondary form__button--max-width"
            >
                Volgende stap
            </BaseButton>

            <BaseButton
                v-else-if="isFreeAccountSignup"
                :disabled="isSubmitting"
                type="submit"
                class="form__submit button--secondary form__button--max-width"
            >
                Bevestigen
            </BaseButton>

            <p>
                Heb je al een account? <a @click.prevent="$emit('login')">Login</a>
            </p>
        </template>
    </BaseForm>
</template>

<script setup lang="ts">
import type { Maybe } from '~/types/craft-schema';
import { useForm, Field as FieldValidator } from 'vee-validate';
import { FORM_REGULAR, FORM_FREE } from '@/constants/signUpFormTypes.js';
import {
    ACTIVE as MEMBERSHIP_ACTIVE,
    INACTIVE as MEMBERSHIP_INACTIVE,
    UNKNOWN as MEMBERSHIP_UNKNWON,
} from '@/constants/userMembershipStatus.js';

const props = withDefaults(
    defineProps<{
        formData: FormDataRecord;
        authorization?: Maybe<string>;
        customFieldEnabled?: Maybe<boolean>;
        customFieldLabel?: Maybe<string>;
        customFieldDescription?: Maybe<string>;
        formType?: Maybe<string>;
        campaign?: Maybe<number>;
        school?: Maybe<string>;
        studentNumberEnabled?: Maybe<boolean>;
        studentNumberLabel?: Maybe<string>;
        studentNumberHelpText?: Maybe<string>;
    }>(),
    {
        formType: FORM_REGULAR,

        authorization: undefined,
        customFieldEnabled: undefined,
        customFieldLabel: undefined,
        customFieldDescription: undefined,
        campaign: undefined,
        school: undefined,
        studentNumberEnabled: false,
        studentNumberLabel: undefined,
        studentNumberHelpText: undefined,
    }
);

const emit = defineEmits(['submit', 'login']);

const isSubmitting = ref(false);
const membershipStatus = ref<string|null>(null);
const submissionErrorMessage = ref<string|null>(null);

const isFreeAccountSignup = computed(() => props.formType === FORM_FREE);

interface AccountDetailsForm {
    email: string;
    password: string;
    password_confirmation: string;
    custom: string;
    studentNumber: string;
}

const { handleSubmit } = useForm<AccountDetailsForm>({
    validationSchema: {
        email: 'email',
        password: 'required|min:8',
        password_confirmation: 'required|min:8',
        custom: null,
        studentNumber: props.studentNumberEnabled ? 'required' : null,
    },
    initialValues: {
        email: props.formData?.email,
        password: props.formData?.password,
        password_confirmation: props.formData?.password_confirmation,
    }
});

const consentGiven = ref(!!props.formData.consentGiven);

const ERROR_ONLY_NEW_MEMBERS = 'ONLY_NEW_MEMBERS' as const;
const ERROR_EMAIL_DOMAIN_NOT_ALLOWED = 'EMAIL_DOMAIN_NOT_ALLOWED' as const;

const checkEmail = async(email: string) => {
    const { data, error } = await useFetchLaravel<{
        success: boolean,
        membershipStatus: typeof MEMBERSHIP_ACTIVE | typeof MEMBERSHIP_INACTIVE | typeof MEMBERSHIP_UNKNWON,
        error: typeof ERROR_ONLY_NEW_MEMBERS | typeof ERROR_EMAIL_DOMAIN_NOT_ALLOWED
    }>('/v2.0/user/email-confirmation/send', {
        method: 'POST',
        data: {
            email: email,
            campaign: props.campaign || null,
            isFreeAccountSignup: isFreeAccountSignup.value,
        }
    });

    if (error.value) {
        submissionErrorMessage.value = 'Er ging iets mis…';
    } else if (data.value?.success === false) {
        submissionErrorMessage.value = 'E-mailadres is al in gebruik…';
    }

    isSubmitting.value = false;

    return data.value;
};

const submitAccountDetails = handleSubmit(async(formData) => {
    submissionErrorMessage.value = null;

    if (formData.password !== formData.password_confirmation) {
        submissionErrorMessage.value = 'De wachtwoorden komen niet overeen';
        return;
    }

    if (!consentGiven.value) {
        submissionErrorMessage.value = 'Accepteer de voorwaarden om door te gaan';
        return;
    }

    isSubmitting.value = true;

    const emailCheckResponse = await checkEmail(formData.email);

    if (emailCheckResponse?.error && emailCheckResponse.error === ERROR_ONLY_NEW_MEMBERS) {
        submissionErrorMessage.value = 'Deze aanbieding is alleen voor nieuwe aanmeldingen. ' +
            'Wil je je oude lidmaatschap heractiveren? ' +
            '<a href="/heractiveer"><i><u>Klik hier</u></i></a>';
        return;
    } else if (emailCheckResponse?.error && emailCheckResponse.error === ERROR_EMAIL_DOMAIN_NOT_ALLOWED) {
        submissionErrorMessage.value = 'Met dit e-mailadres kun je je niet aanmelden via deze pagina.';
        return;
    } else if (!emailCheckResponse?.success) {
        return;
    }

    if (
        emailCheckResponse?.membershipStatus === MEMBERSHIP_ACTIVE ||
        emailCheckResponse?.membershipStatus === MEMBERSHIP_INACTIVE ||
        emailCheckResponse?.membershipStatus === MEMBERSHIP_UNKNWON
    ) {
        membershipStatus.value = emailCheckResponse.membershipStatus as string;

        useSignUpStore().setMembershipStatus(membershipStatus.value);
    }

    if (emailCheckResponse?.success === true) {
        useSignUpStore().setEmail(formData.email);

        isSubmitting.value = false;

        emit('submit', {
            password: formData.password,
            password_confirmation: formData.password_confirmation,
            email: formData.email,
            consentGiven: consentGiven.value,
            customField: formData.custom,
            studentNumber: props.studentNumberEnabled ? `[${props.school}] ${formData.studentNumber}` : null
        });
    } else if (
        emailCheckResponse?.success === false &&
        emailCheckResponse?.membershipStatus === MEMBERSHIP_ACTIVE
    ) {
        isSubmitting.value = false;
        membershipStatus.value = MEMBERSHIP_ACTIVE;
    }
});
</script>

<style lang="less" src="./FormAccountDetails.less" />
